import Make from './make'

const make = Make('settings/')

interface InviteContextData {
  token: string
}

export interface InviteContext {
  accountName: string
  companyName: string
  inviterEmail: string
  inviterName: string
  inviteeEmail?: string
}

export const getInviteContext = async (
  data: InviteContextData,
): Promise<any> => {
  const req = await make.post({
    endpoint: 'invite-context',
    data,
  })

  if (!req.success) {
    return false
  }

  return req
}

export const defaultEnterpriseFields: EnterpriseBillingDetails = {
  companyName: '',
  businessDivision: '',
  autoRenew: true,
  sponsorList: [],
  accountManagerList: [],
  contractStartDate: null,
  contractEndDate: null,
  revenuePerMonth: 0,
  totalContractValue: 0,
  breakClauseDate: null,
  domainLimit: 999,
  userLimit: 0,
  domains: '',
  legalEntityName: '',
  customTandC: false,
  companyNotes: '',
  dataSourceType: 'GA_VIEW',
  trackAvailable: true,
  auditAvailable: true,
  reportAvailable: true,
  explainAvailable: true,
}

export const connectToGoogle = async (applicationState = {}): Promise<void> => {
  const req = await make.post<{ GOOGLE_AUTH_URL: string }>({
    endpoint: 'create-google-auth-url',
    data: {
      state: applicationState,
    },
  })

  const googleRedirect = req?.res?.data?.GOOGLE_AUTH_URL

  if (!req.success || !googleRedirect) {
    throw new Error('Failed to connect to Google')
  }

  window.location.assign(googleRedirect)
}

export const connectToAdobe = async (applicationState = {}): Promise<void> => {
  const req = await make.post<{ ADOBE_AUTH_URL: string }>({
    endpoint: 'create-adobe-auth-url',
    data: {
      state: applicationState,
    },
  })

  const adobeRedirect = req?.res?.data?.ADOBE_AUTH_URL

  if (!req.success || !adobeRedirect) {
    throw new Error('Failed to connect to Adobe')
  }

  window.location.assign(adobeRedirect)
}

export const handleGoogleResponse = async (googleParams = '') => {
  const req = await make.get<{ state: string }>({
    endpoint: `handle-ga-oauth2callback${googleParams}`,
  })

  return req
}

export const handleAdobeResponse = async (adobeParams = '') => {
  const req = await make.get<{ state: string }>({
    endpoint: `handle-aa-oauth2callback${adobeParams}`,
  })

  return req
}

export interface AssociatedView {
  id: string
  name: string
}

export interface AssociatedProperty {
  id: string
  name: string
  views: AssociatedView[]
}

export interface AssociatedAccount {
  id: string
  name: string
  webProperties: AssociatedProperty[]
}

interface GetAssociatedAccounts {
  credentials: string
}

export const getAssociatedAccounts = async (
  data: GetAssociatedAccounts,
): Promise<AssociatedAccount[] | []> => {
  const req = await make.post<{ accountSummary: AssociatedAccount[] }>({
    endpoint: 'ga-accountdata-list',
    data: {
      ...data,
    },
  })

  if (!req.success) {
    return []
  }

  return req?.res?.data?.accountSummary || []
}

export interface AdobeSuite {
  adobeReportSuiteId: string
  adobeReportSuiteName: string
}

export interface AdobeAccount {
  adobeCompanyName: string
  adobeGlobalCompanyId: string
  adobeImsOrgId: string
  reportSuites: AdobeSuite[]
}

interface GetAdobeReportSuites {
  credentials: string
}

export const getAdobeReportSuites = async (
  data: GetAdobeReportSuites,
): Promise<AdobeAccount[] | []> => {
  const req = await make.post<{ reportSuiteList: AdobeAccount[] }>({
    endpoint: 'list-adobe-report-suites',
    data: {
      ...data,
    },
  })

  if (!req.success) {
    return []
  }

  return req?.res?.data?.reportSuiteList || []
}

export const addEnterpriseAccount = async (
  data: EnterpriseBillingDetails,
): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'add-enterprise-account',
    data,
  })

  if (!req.success) {
    return false
  }
  return true
}

interface ReconnectAA {
  adobeCompanyName: string
  adobeGlobalCompanyId: string
  adobeImsOrgId: string
  adobeReportSuiteId: string
  adobeReportSuiteName: string
  credentials: string
}

export const reconnectAA = async (data: ReconnectAA): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'reconnect-aa',
    data,
  })

  if (!req.success) {
    return false
  }
  return true
}

interface ReconnectGA {
  credentials: string
  accountID: string
}

export const reconnectGA = async (data: ReconnectGA): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'reconnect-ga',
    data,
  })

  if (!req.success) {
    return false
  }
  return true
}

interface UpdateUserPermissions {
  userID: string
  permissionLevel: 'admin' | 'regular'
  accountID: string
}

export const updateUserPermissions = async (
  data: UpdateUserPermissions,
): Promise<boolean> => {
  const req = await make.put({
    endpoint: 'update-user-permissions',
    data: {
      ...data,
      uid: data.userID,
    },
  })

  if (!req.success) {
    return false
  }

  return true
}

interface UpdateAccountDescription {
  accountID: string
  accountName?: string
  websiteURL?: string
}

export const updateAccountDescription = async (
  data: UpdateAccountDescription,
): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'update-account-description',
    data: {
      ...data,
    },
  })

  if (!req.success) {
    return false
  }

  return true
}

interface AddNewUserToAccount {
  email: string
  permissionLevel: PermissionLevel
  accountID: string
}

export const addNewUserToAccount = async (
  data: AddNewUserToAccount,
): Promise<boolean | any> => {
  const req = await make.post({
    endpoint: 'add-user',
    data,
  })
  // eslint-disable-next-line
  // debugger
  if (!req.success) {
    return false
  }

  return req?.res?.data || false
}

interface RevokeEmailInvite {
  email: string
}

export const revokeEmailInvite = async (
  data: RevokeEmailInvite,
): Promise<boolean> => {
  const req = await make.delete({
    endpoint: 'revoke-registration',
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

interface RemoveUser {
  userID: string
  accountID: string
}

export const removeUser = async (data: RemoveUser): Promise<boolean> => {
  const req = await make.delete({
    endpoint: 'remove-user',
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

interface RemoveAccount {
  accountID: string
}

export const removeAccount = async (data: RemoveAccount): Promise<boolean> => {
  const req = await make.delete({
    endpoint: `remove-account/${data.accountID}`,
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

export interface ConnectionStatus {
  accountConnectionStatus: {
    accountID: string
    billingState: string
    companyID: string
    connected: boolean
    homepage: string
    name: string
    permission: string
  }[]
}

export const getAllAccountsConnectionStatus = async (): Promise<ConnectionStatus | null> => {
  const req = await make.get<ConnectionStatus>({
    endpoint: 'get-ga-connection-status',
  })

  if (!req.success) {
    return null
  }

  return req?.res?.data || null
}

export interface EmailNotification {
  emailStatus: {
    auditEmail: boolean
    explainEmail: boolean
    reportEmail: boolean
    trackEmail: boolean
  }
}

export const getEmailNotifications = async (): Promise<EmailNotification | null> => {
  const req = await make.get<EmailNotification>({
    endpoint: 'get-email-settings',
  })

  if (!req.success) {
    return null
  }

  return req?.res?.data || null
}

export interface ToggleEmailNotifications {
  module: 'audit' | 'explain' | 'report' | 'track'
  accID: string
}

export const toggleEmailNotifications = async (
  data: ToggleEmailNotifications,
): Promise<boolean> => {
  const req = await make.put({
    endpoint: `toggle-email-settings/${data.module}/${data.accID}`,
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

export const removeAllAccounts = async (): Promise<boolean> => {
  const req = await make.delete({
    endpoint: 'remove-accounts',
  })

  if (!req.success) {
    return false
  }

  return true
}

export interface ToggleModuleAvailability {
  module: 'audit' | 'explain' | 'report' | 'track'
}

export const toggleModuleAvailability = async (
  data: ToggleModuleAvailability,
): Promise<boolean> => {
  const req = await make.put({
    endpoint: `toggle-module-availability/${data.module}`,
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

export interface AddAccount {
  gaAccountID: string
  gaAccountName: string
  gaPropertyID: string
  gaPropertyName: string
  gaViewID: string
  gaViewName: string
  credentials: string
}

export const addAccount = async (data: AddAccount): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'add-ga-account',
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}

export const addFirstEnterpriseAccount = async (
  data: AddAccount,
): Promise<boolean> => {
  const req = await make.post({
    endpoint: 'add-first-ga-account',
    data,
  })

  if (!req.success) {
    return false
  }

  return true
}
